import { useGetOurStoryArticles } from 'common/hooks/useGetOurStoryArticles'
import { useEffect, useState } from 'react'
import { BsFillCaretLeftFill, BsFillCaretRightFill } from 'react-icons/bs'
import { motion, AnimatePresence } from 'framer-motion'

const slideVariants = {
  initial: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%', // Start off-screen based on direction
    opacity: 0,
  }),
  animate: {
    x: 0, // Slide into view
    opacity: 1,
    transition: { duration: 0.5 },
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%', // Slide out of view based on direction
    opacity: 0,
    transition: { duration: 0.4 },
  }),
}

const bpWidths = 'lg:w-[864px] 2xl:w-[1280px] 3xl:w-[1680px]'

export const Section11 = () => {
  const { getArticles, articles, isLoadMore } = useGetOurStoryArticles()
  const [currentPage, setCurrentPage] = useState(1)
  const [displayedArticles, setDisplayedArticles] = useState<any[]>([])
  const [direction, setDirection] = useState(1) // Tracks the direction of the animation
  const [isAnimating, setIsAnimating] = useState(false) // Prevents multiple animations at once
  const [hasLoaded, setHasLoaded] = useState(false) // Tracks if the first load has completed

  // Load the first set of articles when the component mounts
  useEffect(() => {
    getArticles().then(() => setHasLoaded(true))
  }, [])

  // Update displayed articles whenever the current page changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * 3
    const endIndex = startIndex + 3
    setDisplayedArticles(articles.slice(startIndex, endIndex))
  }, [currentPage, articles])

  const handleForward = () => {
    if (isAnimating || !hasLoaded) return // Prevent triggering another animation while one is running
    setDirection(1)
    if (currentPage * 3 >= articles.length && isLoadMore) {
      // Fetch the next set of articles if not already loaded
      getArticles()
    }
    setCurrentPage((prevPage) => prevPage + 1)
  }

  const handleBack = () => {
    if (isAnimating || !hasLoaded) return // Prevent triggering another animation while one is running
    setDirection(-1)
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1)) // Ensure we don't go below page 1
  }

  return (
    <>
      <div className='flex flex-col w-full items-center p-10'>
        <div
          className={`relative overflow-hidden w-full h-[632px] md:h-[184px] 3xl:h-[284px] ${bpWidths}`}
        >
          <AnimatePresence
            custom={direction}
            onExitComplete={() => setIsAnimating(false)}
          >
            <motion.div
              key={currentPage} // Ensure a new animation is triggered when the page changes
              custom={direction}
              variants={slideVariants}
              initial='initial'
              animate='animate'
              exit='exit'
              className='flex flex-col justify-center items-center gap-10 md:flex-row lg:justify-between 2xl:gap-20 3xl:gap-30'
              onAnimationStart={() => {
                if (hasLoaded) setIsAnimating(true) // Prevent new animations while running
              }}
            >
              {displayedArticles.map((article, index) => (
                <div
                  className='rounded-xl shadow-lg p-[2px] w-[270px] lg:w-[33%] bg-gradient-to-b from-[#A9A9FF] from-0% to-[#65C2D7] to-100%'
                  key={index}
                >
                  <div className='bg-white p-6 w-full h-[180px] 3xl:h-[280px] rounded-[11px] flex flex-col justify-between'>
                    <h5 className='text-[#001A3D] text-sm font-medium lg:text-base lg:leading-6 lg:tracking-[0.4px] 2xl:text-lg 3xl:text-2xl'>
                      {article.title}
                    </h5>
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='text-[#001A3D] text-xs font-medium self-end tracking-[0.4px] 2xl:text-base hover:underline'
                    >
                      Read article
                    </a>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className={`mt-8 flex justify-center lg:justify-end ${bpWidths}`}>
          <div className='flex flex-row justify-between'>
            <button
              onClick={handleBack}
              disabled={currentPage === 1}
              className={`${currentPage === 1
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-[#001A3D]'
                }`}
            >
              <BsFillCaretLeftFill className='w-6 h-6 ' />
            </button>
            <button
              onClick={handleForward}
              disabled={!isLoadMore && currentPage * 3 >= articles.length}
              className={`${!isLoadMore && currentPage * 3 >= articles.length
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-[#001A3D]'
                }`}
            >
              <BsFillCaretRightFill className='w-6 h-6' />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
